
import { useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { forceRefreshApplication } from "@/utils/cacheManager";
import { safeToast } from "@/components/ui/use-toast";

/**
 * Hook for handling service query error recovery
 */
export const useErrorRecovery = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const errorRetryCountRef = useRef<number>(0);

  /**
   * Reset the error retry count
   */
  const resetErrorCount = () => {
    errorRetryCountRef.current = 0;
  };

  /**
   * Handle multiple consecutive failures
   */
  const handleMultipleFailures = (clearCacheAndRetry: () => void) => {
    errorRetryCountRef.current += 1;
    
    // If we've failed too many times in a row, take more drastic action
    if (errorRetryCountRef.current >= 3) {
      // Show toast with option to force refresh using non-JSX action
      safeToast({
        title: "Connection Issues",
        description: "Having trouble loading data. Would you like to refresh?",
        variant: "destructive",
        action: {
          label: "Refresh",
          onClick: () => forceRefreshApplication(queryClient)
        },
        duration: 10000,
      });
      
      // Reset error count so we don't spam the user
      errorRetryCountRef.current = 0;
    } else if (errorRetryCountRef.current === 2) {
      // On second failure, try clearing cache and retry
      clearCacheAndRetry();
    }
  };

  /**
   * Handle recovery success
   */
  const handleRecoverySuccess = (hasAlreadyRecovered: boolean) => {
    // Only show recovery message once
    if (!hasAlreadyRecovered && errorRetryCountRef.current > 1) {
      toast({
        title: "Connection Restored",
        description: "We've restored your connection to the service.",
        duration: 3000,
      });
      
      // Reset error counter
      resetErrorCount();
    }
  };

  return {
    errorRetryCountRef,
    handleMultipleFailures,
    handleRecoverySuccess,
    resetErrorCount
  };
};
